import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Pencil, Check, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/useConfirm";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeriodBar } from "@/components/PeriodBar";
import { dayLabel, money, rangeFor, weekDays, ymd } from "@/lib/periods";

const UNITS = ["portion", "each", "kg", "g", "L", "ml", "tray", "slice", "loaf", "bag"];
const REASONS = ["Overproduction", "Spoilage / Expired", "Prep waste", "Burnt / Cooking error", "Wrong order", "Customer return", "Dropped / Damaged", "Other"];
const STAGES = ["Prep", "Cooking", "Service", "Storage", "Other"];

const emptyForm = (user) => ({
  site: user?.primary_site || "SITE_1",
  date: ymd(new Date()),
  time: "",
  item: "",
  qty: "",
  unit: "portion",
  reason: "Overproduction",
  stage: "Service",
  unit_cost: "",
  recorded_by: user?.name || "",
});

function EntryDialog({ open, onClose, initial, sites, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial); }, [open, initial]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const liveTotal = (Number(form.qty) || 0) * (Number(form.unit_cost) || 0);
  const editing = Boolean(form.id);

  const save = async () => {
    if (!form.item.trim() || !form.date || !(Number(form.qty) > 0)) {
      toast.error("Item, date and a quantity above 0 are required");
      return;
    }
    setBusy(true);
    const payload = {
      site: form.site, date: form.date, time: form.time, item: form.item.trim(),
      qty: Number(form.qty), unit: form.unit, reason: form.reason, stage: form.stage,
      unit_cost: Number(form.unit_cost) || 0, recorded_by: form.recorded_by,
    };
    try {
      if (editing) await api.patch(`/wastage/entries/${form.id}`, payload);
      else await api.post("/wastage/entries", payload);
      toast.success(editing ? "Entry updated" : "Wastage recorded");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wastage-entry-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit wastage entry" : "Record food wastage"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Site</Label>
            <Select value={form.site} onValueChange={set("site")}>
              <SelectTrigger data-testid="wastage-form-site"><SelectValue /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} data-testid="wastage-form-date" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Time (optional)</Label>
            <Input type="time" value={form.time} onChange={(e) => set("time")(e.target.value)} data-testid="wastage-form-time" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Recorded by</Label>
            <Input value={form.recorded_by} onChange={(e) => set("recorded_by")(e.target.value)} data-testid="wastage-form-recorded-by" />
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <Label className="text-xs">Item / dish wasted</Label>
            <Input placeholder="e.g. Chicken Caesar Salad" value={form.item} onChange={(e) => set("item")(e.target.value)} data-testid="wastage-form-item" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Qty</Label>
            <Input type="number" min="0" step="0.1" value={form.qty} onChange={(e) => set("qty")(e.target.value)} data-testid="wastage-form-qty" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Unit</Label>
            <Select value={form.unit} onValueChange={set("unit")}>
              <SelectTrigger data-testid="wastage-form-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Reason</Label>
            <Select value={form.reason} onValueChange={set("reason")}>
              <SelectTrigger data-testid="wastage-form-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Stage</Label>
            <Select value={form.stage} onValueChange={set("stage")}>
              <SelectTrigger data-testid="wastage-form-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Unit cost ($)</Label>
            <Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost")(e.target.value)} data-testid="wastage-form-unit-cost" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Total cost (auto)</Label>
            <div className="h-9 flex items-center px-3 rounded-md bg-[#C9A84C]/10 border border-[#C9A84C]/40 font-mono text-sm" data-testid="wastage-form-total">
              {money(liveTotal)}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1A2744] text-white" data-testid="wastage-form-save">
            {editing ? "Save changes" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignoffButton({ entry, kind, onDone }) {
  const signed = entry[`${kind}_signoff`];
  const label = kind === "kitchen" ? "Kitchen" : "FOH";
  const toggle = async () => {
    try {
      await api.post(`/wastage/entries/${entry.id}/signoff`, { kind });
      toast.success(signed ? `${label} sign-off cleared` : `${label} signed off`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <button
      onClick={toggle}
      title={signed ? `${label}: ${signed.by} · ${new Date(signed.at).toLocaleString()}` : `Sign off (${label})`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
        signed ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-gray-500 border-gray-300 hover:border-[#1A2744]"
      }`}
      data-testid={`signoff-${kind}-${entry.id}`}
    >
      {signed && <Check className="h-3 w-3" />}{label}{signed ? ` · ${signed.by.split(" ")[0]}` : ""}
    </button>
  );
}

export default function FoodWastage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = ["ADMIN", "SUPERADMIN"].includes(user?.role);
  const [mode, setMode] = useState("WEEK");
  const [anchor, setAnchor] = useState(new Date());
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [site, setSite] = useState("ALL");
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(null); // form initial values or null

  const range = useMemo(
    () => (mode === "CUSTOM" ? (custom.start && custom.end ? custom : null) : rangeFor(mode, anchor)),
    [mode, anchor, custom]
  );

  useEffect(() => {
    api.get("/config").then(({ data }) => setSites(data.sites || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!range) return;
    const params = { start: range.start, end: range.end };
    if (site !== "ALL") params.site = site;
    try {
      const [a, b] = await Promise.all([
        api.get("/wastage/entries", { params }),
        api.get("/wastage/summary", { params }),
      ]);
      setEntries(a.data.entries || []);
      setSummary(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [range?.start, range?.end, site]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await confirm({ title: "Delete entry?", description: `Remove "${row.item}" (${money(row.total_cost)}).`, confirmText: "Delete" });
    if (!ok) return;
    try { await api.delete(`/wastage/entries/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const byDay = useMemo(() => {
    const map = {};
    entries.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [entries]);
  const days = mode === "WEEK" && range ? weekDays(range) : Object.keys(byDay).sort();
  const totalCost = summary?.total_cost || 0;
  const topReason = summary?.by_reason?.[0];
  const siteLabel = (c) => sites.find((s) => s.code === c)?.label || c;

  return (
    <div className="space-y-6" data-testid="food-wastage-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] flex items-center gap-3">
            <UtensilsCrossed className="h-8 w-8 text-[#C9A84C]" /> Food Wastage
          </h1>
          <p className="text-gray-500 mt-1">Daily wastage record — Mon to Sun, live totals.</p>
        </div>
        <Button onClick={() => setDialog(emptyForm(user))} className="bg-[#1A2744] text-white" data-testid="wastage-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Record wastage
        </Button>
      </header>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <PeriodBar mode={mode} setMode={setMode} anchor={anchor} setAnchor={setAnchor} custom={custom} setCustom={setCustom} range={range} idPrefix="wastage-period" />
        <div className="lg:ml-auto">
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="w-[170px] h-8" data-testid="wastage-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-[#C9A84C]/50">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Total wastage cost</div>
            <div className="text-3xl font-bold text-[#1A2744] mt-1" data-testid="wastage-total-cost">{money(totalCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Entries</div>
            <div className="text-3xl font-bold text-[#1A2744] mt-1" data-testid="wastage-entry-count">{summary?.entries || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Top reason</div>
            <div className="text-lg font-semibold text-[#1A2744] mt-1" data-testid="wastage-top-reason">
              {topReason ? `${topReason.reason} · ${money(topReason.total)}` : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      {summary?.by_reason?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="wastage-reason-chips">
          {summary.by_reason.map((r) => (
            <Badge key={r.reason} variant="outline" className="border-gray-300 text-xs">
              {r.reason}: <span className="font-semibold ml-1">{money(r.total)}</span>
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {days.length === 0 && (
          <Card><CardContent className="p-10 text-center text-gray-400 text-sm">No wastage recorded in this period.</CardContent></Card>
        )}
        {days.map((d) => {
          const rows = byDay[d] || [];
          const dayTotal = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
          return (
            <Card key={d} data-testid={`wastage-day-${d}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-2.5 bg-[#1A2744]/[0.04] border-b border-gray-100">
                  <span className="text-sm font-semibold text-[#1A2744]">{dayLabel(d)}</span>
                  <span className="text-sm font-mono font-semibold text-[#1A2744]" data-testid={`wastage-day-total-${d}`}>{money(dayTotal)}</span>
                </div>
                {rows.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-gray-400">No entries</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[70px]">Time</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Stage</TableHead>
                          <TableHead>Site</TableHead>
                          <TableHead className="text-right">Unit $</TableHead>
                          <TableHead className="text-right">Total $</TableHead>
                          <TableHead>Recorded by</TableHead>
                          <TableHead>Sign-off</TableHead>
                          {isAdmin && <TableHead />}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.id} data-testid={`wastage-row-${r.id}`}>
                            <TableCell className="text-xs text-gray-500">{r.time || "—"}</TableCell>
                            <TableCell className="font-medium text-sm">{r.item}</TableCell>
                            <TableCell className="text-xs">{r.qty} {r.unit}</TableCell>
                            <TableCell className="text-xs text-gray-600">{r.reason || "—"}</TableCell>
                            <TableCell className="text-xs text-gray-600">{r.stage || "—"}</TableCell>
                            <TableCell className="text-xs text-gray-500">{siteLabel(r.site)}</TableCell>
                            <TableCell className="text-right text-xs font-mono">{money(r.unit_cost)}</TableCell>
                            <TableCell className="text-right text-sm font-mono font-semibold">{money(r.total_cost)}</TableCell>
                            <TableCell className="text-xs text-gray-600">{r.recorded_by || "—"}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <SignoffButton entry={r} kind="kitchen" onDone={load} />
                                <SignoffButton entry={r} kind="foh" onDone={load} />
                              </div>
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDialog({ ...r, qty: String(r.qty), unit_cost: String(r.unit_cost) })} data-testid={`wastage-edit-${r.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(r)} data-testid={`wastage-delete-${r.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {dialog && <EntryDialog open onClose={() => setDialog(null)} initial={dialog} sites={sites} onSaved={load} />}
    </div>
  );
}
